/* © CNES */
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import './App.scss';
import { AuthenticationController } from './components/authentication/AuthenticationController';
import { Explore } from './components/explore/Explore';
import { MyData } from './components/myData/MyData';
import { MyDataTabs } from './components/myData/MyDataTabs/MyDataTabs';
import { MyInformation } from './components/myInformation/MyInformation';
import { NotifToast } from './components/shared/Toast/NotifToast';
import { ConfigContextProvider } from './context/ConfigContext';
import { FilterDataContextProvider } from './context/FilterDataContext';
import { Cookies } from './cookies/Cookies';
import { PageMaintenance } from './maintenance/PageMaintenance';
import { PageNotFound } from './maintenance/PageNotFound/PageNotFound';
import { PageRateLimiting } from './maintenance/PageRateLimiting/PageRateLimiting';

/**
 * GeoDataHub Main Application file.
 * @returns Element.
 */
const App = () => {
  const [visible, setVisible] = useState(false);
  const { trackPageView } = useMatomo();
  // Message display from runtime var
  const displayMessage =
    process.env.NODE_ENV === 'production'
      ? window.__RUNTIME_CONFIG__?.DISPLAY_MESSAGE === 'true'
      : false;
  useEffect(() => {
    // Track Page View with Matomo: https://github.com/jonkoops/matomo-tracker/tree/main/packages/react
    trackPageView();
    if (displayMessage) {
      setVisible(true);
    }
    // eslint-disable-next-line
  }, [displayMessage]);
  // Application falls in maintenance from runtime var.
  const maintenance =
    process.env.NODE_ENV === 'production'
      ? window.__RUNTIME_CONFIG__?.MAINTENANCE_MODE === 'true'
      : false;
  // Create a client
  const queryClient = new QueryClient();
  moment.updateLocale('fr', {
    months:
      'janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre'.split(
        '_'
      ),
    monthsShort:
      'janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.'.split(
        '_'
      ),
    monthsParseExact: true,
    weekdays: 'dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi'.split('_'),
    weekdaysShort: 'dim._lun._mar._mer._jeu._ven._sam.'.split('_'),
    weekdaysMin: 'Di_Lu_Ma_Me_Je_Ve_Sa'.split('_'),
    weekdaysParseExact: true,
    longDateFormat: {
      LT: 'HH:mm',
      LTS: 'HH:mm:ss',
      L: 'DD/MM/YYYY',
      LL: 'D MMMM YYYY',
      LLL: 'D MMMM YYYY HH:mm',
      LLLL: 'dddd D MMMM YYYY HH:mm',
    },
    calendar: {
      sameDay: '[Aujourd’hui à] LT',
      nextDay: '[Demain à] LT',
      nextWeek: 'dddd [à] LT',
      lastDay: '[Hier à] LT',
      lastWeek: 'dddd [dernier à] LT',
      sameElse: 'L',
    },
    relativeTime: {
      future: 'dans %s',
      past: 'il y a %s',
      s: 'quelques secondes',
      m: 'une minute',
      mm: '%d minutes',
      h: 'une heure',
      hh: '%d heures',
      d: 'un jour',
      dd: '%d jours',
      M: 'un mois',
      MM: '%d mois',
      y: 'un an',
      yy: '%d ans',
    },
    dayOfMonthOrdinalParse: /\d{1,2}(er|e)/,
    ordinal: function (number) {
      return number + (number === 1 ? 'er' : 'e');
    },
    meridiemParse: /PD|MD/,
    isPM: function (input) {
      return input.charAt(0) === 'M';
    },
    // In case the meridiem units are not separated around 12, then implement
    // this function (look at locale/id.js for an example).
    // meridiemHour : function (hour, meridiem) {
    //     return /* 0-23 hour, given meridiem token and hour 1-12 */ ;
    // },
    meridiem: function (hours, minutes, isLower) {
      return hours < 12 ? 'PD' : 'MD';
    },
    week: {
      dow: 1, // Monday is the first day of the week.
      doy: 4, // Used to determine first week of the year.
    },
  });

  const router = createBrowserRouter([
    {
      path: '/search',
      element: <Explore />,
    },
    {
      path: '/',
      element: <Explore />,
      errorElement: <PageNotFound />,
    },
    {
      path: '/myData',
      element: <MyData />,
      children: [
        {
          path: '/myData/processings',
          element: (
            <MyDataTabs
              activeIndex={1}
              setActiveIndex={() => {}}
              setDownloadProgress={() => {}}
            />
          ),
        },
      ],
    },
    {
      path: '/myInformation',
      element: <MyInformation />,
    },
    {
      path: '/maintenance',
      element: <PageMaintenance />,
    },
    {
      path: '/ratelimiting',
      element: <PageRateLimiting />,
    },
  ]);

  return (
    <>
      {maintenance ? (
        <BrowserRouter>
          <PageMaintenance />
        </BrowserRouter>
      ) : (
        <QueryClientProvider client={queryClient}>
          <ConfigContextProvider>
            <FilterDataContextProvider>
              <AuthenticationController />
              <RouterProvider router={router} />
              <Dialog
                header="Information message"
                visible={visible}
                className="DialogInfo"
                onHide={() => setVisible(false)}
              >
                <p className="m-0">
                  {displayMessage && window.__RUNTIME_CONFIG__?.MESSAGE}
                </p>
              </Dialog>
              <Cookies></Cookies>
            </FilterDataContextProvider>
            <NotifToast
              summaryTitle="Request failed"
              displayMessage="Ressource not found"
              displaySeverity="error"
            />
          </ConfigContextProvider>
        </QueryClientProvider>
      )}
    </>
  );
};

export default App;
